import { get as getCookie } from 'js-cookie';

import { getApplicationEndpoint } from '@gitbook/env';
import { Logger } from '@gitbook/util-debug';

import { VISITORID_COOKIE } from '../common';

const logger = Logger('app:analytics');

let visitorId: string | null = null;
let pending: Promise<any> | null = null;

/**
 * Return a visitorId that has been synchronized between other GitBook sessions.
 * This identifier represents the current visitor, it is independent from the user ID when the user is logged-in.
 */
export function useVisitorIdentifier(): string {
    if (typeof window === 'undefined') {
        throw new Error('Hook "useVisitorIdentifier" cannot be called during server rendering');
    }

    if (pending) {
        throw pending;
    }

    if (typeof visitorId !== 'string') {
        throw new Error(
            `Visitor ID is not set. Please call "initializeVisitorID" before calling "useVisitorIdentifier"`
        );
    }

    return visitorId;
}

/**
 * Initialize the visitor ID.
 */
export async function initializeVisitorID(disableCookies = false): Promise<string> {
    if (disableCookies) {
        // If cookies are disabled, we use a new visitor ID every time.
        visitorId = crypto.randomUUID();
        return visitorId;
    }

    pending = fetchVisitorID().then((result) => {
        pending = null;
        visitorId = result;
        return visitorId;
    });

    return pending;
}

/**
 * Return the current visitor ID.
 */
export function getVisitorID(): string {
    if (typeof visitorId !== 'string') {
        throw new Error(
            `Visitor ID is not set. Please call "initializeVisitorID" before calling "getVisitorID"`
        );
    }

    return visitorId;
}

/**
 * Asynchronously return the current visitor ID.
 */
export async function getVisitorIDAsync(): Promise<string> {
    if (pending) {
        return pending;
    }

    if (typeof visitorId !== 'string') {
        throw new Error(
            `Visitor ID is not set. Please call "initializeVisitorID" before calling "getVisitorIDAsync"`
        );
    }

    return visitorId;
}

/**
 * Synchronize the visitor session cookie with the server.
 * This ensures that the `VISITORID_COOKIE` cookie reflects the latest login state.
 */
export async function syncVisitorSession(): Promise<void> {
    await fetchVisitorID({ skipOnCookie: false });
}

/**
 * Get the visitor ID from the cookie.
 */
async function fetchVisitorID(
    options: {
        /** If true, the found value in the cookie will be returned instead of making a server request. */
        skipOnCookie?: boolean;
    } = {}
): Promise<string> {
    const { skipOnCookie = true } = options;
    const existingTrackingCookie = getCookie(VISITORID_COOKIE);

    if (existingTrackingCookie && skipOnCookie) {
        // If the cookie already exists, we'll just use that. Avoids a server request.
        return existingTrackingCookie;
    }

    // No tracking deviceId set, we'll need to consolidate with the server.
    const proposed = existingTrackingCookie ?? `${crypto.randomUUID()}R`;

    const applicationEndpoint = getApplicationEndpoint();
    const url = `${applicationEndpoint}/__session/2/?proposed=${proposed}`;

    try {
        const resp = await fetch(url, {
            method: 'GET', // Use GET to play nicely with SameSite cookies.
            credentials: 'include', // Make sure to send/receive cookies.
            cache: 'no-cache',
            mode: 'cors', // Need to use cors as we are on a different domain.
        });

        const { deviceId } = await resp.json();
        return deviceId;
    } catch (error) {
        logger.critical('failed getting visitor ID', error);
        return proposed;
    }
}
